import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SearchX } from "lucide-react";
import { BriefSubscribe } from "@/components/BriefSubscribe";
import { InviteColleague } from "@/components/InviteColleague";
import { ListingCard, ListingSkeletons } from "@/components/ListingCard";
import { PublicShell } from "@/components/layout/PublicShell";
import { Button, EmptyState, SelectField } from "@/components/system";
import { useAudience } from "@/contexts/AudienceContext";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { isCanberraFacility } from "@/lib/listing-display";
import { areaLabel, indexableAreas } from "@/lib/local-pages";
import { areaPath } from "@/lib/local-seo";
import { usePublicDirectory, usePublicSpecialties } from "@/lib/public-directory";
import { SEO } from "@/lib/seo";

/**
 * Public directory: real Canberra specialists and allied health from
 * GET /v1/public/:citySlug/directory. No synthetic listings.
 */
type TabType = "specialist" | "allied_health";

const TAB_LABEL: Record<TabType, string> = { specialist: "Specialists", allied_health: "Allied health" };

function coerceTab(raw: string | null): TabType {
  return raw === "allied_health" ? "allied_health" : "specialist";
}

export default function Directory() {
  const { city } = useCity();
  const { isGpMode } = useAudience();
  const [params, setParams] = useSearchParams();
  const tab = coerceTab(params.get("type"));
  const suburb = params.get("suburb") || "";
  const specialty = params.get("specialty") || "";
  const query = params.get("q") || "";
  const acceptingFilter = params.get("accepting") === "yes";
  const [qInput, setQInput] = useState(query);

  const { data, isLoading, isError, refetch } = usePublicDirectory(city.slug, {
    type: tab,
    accepting: tab === "specialist" && acceptingFilter ? "yes" : undefined,
    limit: 100,
  });
  const specialties = usePublicSpecialties(city.slug);
  const allItems = useMemo(() => (data?.items ?? []).filter(isCanberraFacility), [data?.items]);
  const meta = data?.meta;

  const areas = useMemo(
    () =>
      tab === "specialist"
        ? indexableAreas(
            allItems.map((item) => ({
              slug: item.slug,
              name: item.name,
              specialty: item.address_json.specialty,
              suburb: item.address_json.suburb,
            })),
          )
        : [],
    [allItems, tab],
  );
  const suburbs = useMemo(
    () =>
      Array.from(new Set(allItems.map((f) => f.address_json.suburb).filter((s): s is string => Boolean(s)))).sort(),
    [allItems],
  );

  const filtered = allItems.filter((f) => {
    if (suburb && f.address_json.suburb !== suburb) return false;
    if (specialty && f.address_json.specialty?.toLowerCase() !== specialty.toLowerCase()) return false;
    if (query) {
      const hay = [
        f.name,
        f.address_json.practice_name,
        f.address_json.suburb,
        f.address_json.street,
        f.address_json.specialty,
        f.address_json.category,
        ...(f.address_json.sub_specialties ?? []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(query.toLowerCase())) return false;
    }
    return true;
  });
  const hasFilters = Boolean(suburb || query || acceptingFilter || specialty);

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const updateTab = (nextTab: TabType) => {
    const next = new URLSearchParams(params);
    next.set("type", nextTab);
    if (nextTab !== "specialist") next.delete("accepting");
    setParams(next, { replace: true });
  };
  const clearFilters = () => {
    setParams(new URLSearchParams({ type: tab }), { replace: true });
    setQInput("");
  };
  const runSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextQuery = qInput.trim();
    track("search", { query_length: nextQuery.length, specialty });
    updateParam("q", nextQuery || null);
  };

  return (
    <>
      <SEO
        title={`${city.name} directory · PulseBoard`}
        description={`Find ${city.name} specialists and allied health providers in a source-attributed clinical directory. No synthetic listings.`}
        path="/directory"
      />

      <PublicShell>
        <div className="ds-container">
          <header className="ds-page-head">
            <p className="ds-eyebrow ds-eyebrow--teal">{city.name} specialist directory</p>
            <h1 className="ds-display-1">{isGpMode ? "Find the right referral option." : `Find a specialist in ${city.name}.`}</h1>
            <p className="ds-lead">
              {isGpMode
                ? "Source-attributed listings with referral details and availability, shown only where a practice has published them."
                : "Every listing shows where its details came from and when they were last checked. You will usually need a GP referral."}
            </p>
            {meta ? (
              <div className="ds-facts">
                <span><b className="ds-num">{meta.specialistCount}</b> specialists</span>
                <span><b className="ds-num">{meta.alliedCount}</b> allied health</span>
                <span><b className="ds-num">{meta.suburbCount}</b> suburbs</span>
              </div>
            ) : null}
          </header>

          {/* Crawlable links into every specialty page (they were orphans before, SEO report 02). */}
          {specialties.data && specialties.data.length > 0 ? (
            <nav className="ds-section ds-section--flush" aria-labelledby="browse-specialty-heading">
              <h2 id="browse-specialty-heading" className="ds-title">Browse by specialty</h2>
              <ul className="ds-link-grid">
                {specialties.data.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/directory/${item.slug}`}
                      onClick={() => track("directory_specialty_click", { specialty_slug: item.slug })}
                    >
                      <span>{item.name}</span>
                      <span className="ds-num">{item.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}

          <section className="ds-section" aria-labelledby="directory-results-heading">
            <div className="ds-section-head">
              <div>
                <h2 id="directory-results-heading" className="ds-display-2">All listings</h2>
              </div>
              <div className="ds-seg" role="group" aria-label="Listing type">
                {(["specialist", "allied_health"] as TabType[]).map((t) => (
                  <button key={t} type="button" aria-pressed={tab === t} onClick={() => updateTab(t)}>
                    {TAB_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>

            <div className="ds-panel ds-panel--tight ds-toolbar">
              <form className="ds-toolbar__search" role="search" onSubmit={runSearch}>
                <label htmlFor="directory-q" className="ds-visually-hidden">Search the directory</label>
                <Search aria-hidden="true" />
                <input
                  id="directory-q"
                  className="ds-input"
                  type="search"
                  value={qInput}
                  onChange={(event) => setQInput(event.target.value)}
                  placeholder="Name, specialty, practice or suburb"
                />
                <Button type="submit">Search</Button>
              </form>
              <div className="ds-toolbar__filters">
                <SelectField
                  label="Suburb"
                  hideLabel
                  value={suburb}
                  onChange={(event) => updateParam("suburb", event.target.value || null)}
                >
                  <option value="">All suburbs</option>
                  {suburbs.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </SelectField>
                {tab === "specialist" ? (
                  <button
                    type="button"
                    className="ds-chip"
                    aria-pressed={acceptingFilter}
                    onClick={() => updateParam("accepting", acceptingFilter ? null : "yes")}
                  >
                    Accepting referrals
                  </button>
                ) : null}
                {hasFilters ? (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>
                ) : null}
                <span className="ds-fine ds-toolbar__count" aria-live="polite">
                  {isLoading ? "Loading…" : isError ? "" : `Showing ${filtered.length} of ${allItems.length}`}
                </span>
              </div>
            </div>

            {isLoading ? (
              <ListingSkeletons />
            ) : isError ? (
              <EmptyState
                tone="error"
                icon={<SearchX />}
                title="The directory didn't load"
                action={<Button variant="secondary" onClick={() => void refetch()}>Try again</Button>}
              >
                Check your connection and try again.
              </EmptyState>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={<SearchX />}
                title={hasFilters ? "No listings match those filters" : `No ${TAB_LABEL[tab].toLowerCase()} listed yet`}
                action={hasFilters ? <Button variant="secondary" onClick={clearFilters}>Clear filters</Button> : undefined}
              >
                {hasFilters
                  ? "Try a broader search or another suburb."
                  : "Listings appear here once their details have been checked against a public source."}
              </EmptyState>
            ) : (
              <div className="ds-grid">
                {filtered.map((facility) => (
                  <ListingCard key={facility.id} facility={facility} />
                ))}
              </div>
            )}
          </section>

          {areas.length > 0 ? (
            <nav className="ds-section" aria-labelledby="browse-area-heading">
              <h2 id="browse-area-heading" className="ds-title">Browse by area</h2>
              <ul className="ds-cluster" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {areas.map((area) => (
                  <li key={area.slug}>
                    <Link to={areaPath(area.slug)} className="ds-chip">
                      {areaLabel(area)} · {area.count}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="ds-small">
                Not sure who to see? <Link className="ds-link" to="/conditions">Which specialist do I need?</Link>
              </p>
            </nav>
          ) : null}

          <div className="ds-split" style={{ marginTop: 16 }}>
            <BriefSubscribe sourcePath="/directory" />
            <InviteColleague />
          </div>
        </div>
      </PublicShell>
    </>
  );
}
